import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import api from '../../../../middleware/api';
import BasicInput from '../../../Common/Inputs/BasicInput/BasicInput';
import { useDrawer } from '../../../Common/SidebarDrawer/SidebarDrawer';
import { toast } from 'react-toastify';
import usersSchema from '../../../../schema/users.schema';
import { getCountryByName, getCountryOptions, getCountryByIso } from '../../../../utils/country.utils';
import Dropdown from '../../../Common/Dropdown/Dropdown';
import { useAuth } from '../../../../context/AuthContext';
import UnifiedPhoneInput from '../../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput';
import { PhoneIcon, UserIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ExpandableSection from '../../../Common/ExpandableSection/ExpandableSection';
import CountryInput from '../../../Common/Inputs/CountryInput/CountryInput';
import ProfilePictureUploader from '../../../Common/Inputs/ProfilePictureUploader/ProfilePictureUploader';

const CreateCustomerForm = ({ setTableData }) => {
    const closeDrawer = useDrawer();
    const countriesOptions = getCountryOptions();
    const navigate = useNavigate();
    const [profilePicture, setProfilePicture] = useState(null);

    const handleCountryChange = (e, setFieldValue) => {
        const isoCountry = e.target.value;
        setFieldValue('address.isoCountry', isoCountry);
        // Marquer le champ comme touché pour déclencher la validation
        setFieldValue('address.touched.isoCountry', true);
    };

    const handleProfilePictureChange = (file) => {
        console.log("Fichier reçu:", file);
        setProfilePicture(file);
    };

    return (
        <Formik
            initialValues={{
                firstname: '',
                lastname: '',
                email: '',
                phone: '',
                phonePrefix: '',
                birthdate: '',
                contactOrigin: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    postcode: '',
                    country: '',
                    isoCountry: '',
                    legalFirstname: '',
                    legalLastname: '',
                },
                isCustomerTherapist: false,
            }}
            validationSchema={usersSchema.createCustomer}

            onSubmit={async (values, { setFieldTouched, setErrors }) => {
                try {
                    values.birthdate === '' ? values.birthdate = null : new Date(values.birthdate).toISOString();

                    if (values.phone === '') {
                        values.phonePrefix = ''
                    }

                    // Récupérer le nom du pays à partir de l'ISO
                    if (values.address.isoCountry) {
                        const country = getCountryByIso(values.address.isoCountry);
                        if (country) {
                            values.address.country = country.name + 'A';
                        } else {
                            setFieldTouched('address.isoCountry', true, true);
                            setErrors({
                                'address.isoCountry': 'Pays invalide'
                            });
                            return
                        }
                    }

                    values.email = values.email.toLowerCase().trim();
                    console.log(values.email); 
                    // Create FormData to handle file upload
                    const formData = new FormData();

                    // Append the profile picture if exists
                    if (profilePicture) {
                        console.log("Ajout de l'image au FormData:", profilePicture);
                        formData.append('file', profilePicture);
                    }
                    // Append user data as JSON
                    formData.append('user', JSON.stringify(values));

                    const response = await api.post('/customers/create', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    toast.success('Client créé avec succès!');
                    closeDrawer();
                    setTableData((prev) => ({
                        ...prev,
                        values: prev ? [response.data, ...prev.values] : [response.data],
                    }));
                    navigate(`/clients/${response.data.id}`)
                } catch (error) {
                    console.log(error);
                    toast.error('Une erreur est survenue lors de la création du client!');
                }
            }}
        >
            {({ handleChange, setFieldValue, values, errors, touched, setFieldTouched, setErrors }) => {
                console.log(errors);
                return (
                    <Form className='flex flex-col gap-4'>
                        <h2 className='text-2xl font-bold m-0 mb-4'>Création d'un client</h2>

                        <ExpandableSection title="Informations du client" defaultExpanded={true} overflow="visible">
                            <div className="grid gap-4 overflow-visible rounded-md">
                                {/* Profile Picture Uploader */}
                                <div className="flex justify-center mb-4">
                                    <ProfilePictureUploader
                                        onImageSelect={handleProfilePictureChange}
                                        error={errors.profilePicture}
                                        touched={touched.profilePicture}
                                    />
                                </div>

                                <BasicInput
                                    placeholder="Prénom *"
                                    name="firstname"
                                    type="text"
                                    value={values.firstname}
                                    onChange={handleChange}
                                    error={errors.firstname}
                                    touched={touched.firstname}
                                    icon={<UserIcon className=" flex-shrink-0 flex-grow-0 h-5 w-5 text-gray-400" />}
                                />
                                <BasicInput
                                    placeholder="Nom *"
                                    name="lastname"
                                    type="text"
                                    value={values.lastname}
                                    onChange={handleChange}
                                    error={errors.lastname}
                                    touched={touched.lastname}
                                    icon={<UserIcon className=" flex-shrink-0 flex-grow-0 h-5 w-5 text-gray-400" />}
                                />
                                <BasicInput
                                    placeholder="Email"
                                    name="email"
                                    type="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                    touched={touched.email}
                                />
                                <UnifiedPhoneInput
                                    name="phone"
                                    value={values.phone}
                                    phonePrefix={values.phonePrefix}
                                    onChange={handleChange}
                                    error={errors.phone}
                                    touched={touched.phone}
                                    placeholder="Téléphone"
                                />
                                <BasicInput
                                    placeholder="Date de naissance"
                                    name="birthdate"
                                    type="date"
                                    value={values.birthdate}
                                    onChange={handleChange}
                                    error={errors.birthdate}
                                    touched={touched.birthdate}
                                />
                                <BasicInput
                                    placeholder="Origine du contact"
                                    name="contactOrigin"
                                    type="text"
                                    value={values.contactOrigin}
                                    onChange={handleChange}
                                    error={errors.contactOrigin}
                                    touched={touched.contactOrigin}
                                />

                                <div className="form-control w-full border border-base-300 border-solid rounded-md hover:border-gray-400 transition-all duration-300 px-1">
                                    <label className="label cursor-pointer space-x-4">
                                        <span className="label-text text-sm font-medium">Est thérapeute</span>
                                        <input
                                            className='checkbox checkbox-primary checkbox-xs border border-solid'
                                            type="checkbox"
                                            name="isCustomerTherapist"
                                            checked={values.isCustomerTherapist}
                                            onChange={handleChange}
                                        />
                                    </label>
                                </div>
                            </div>
                        </ExpandableSection>

                        <ExpandableSection title="Adresse" defaultExpanded={true} overflow="visible">
                            <div className="grid gap-4 overflow-visible rounded-md">
                                <div className="text-sm text-gray-600 mb-2">
                                    <span className="text-red-500">*</span> Champs obligatoires si une adresse est fournie
                                </div>
                                <BasicInput
                                    placeholder="Rue *"
                                    name="address.street"
                                    type="text"
                                    value={values.address.street}
                                    onChange={handleChange}
                                    error={errors?.address?.street}
                                    touched={touched.address?.street}
                                    icon={<MapPinIcon className="h-5 w-5 text-gray-400" />}
                                />
                                <BasicInput
                                    placeholder="Code Postal *"
                                    name="address.postcode"
                                    type="text"
                                    value={values.address.postcode}
                                    onChange={handleChange}
                                    error={errors?.address?.postcode}
                                    touched={touched.address?.postcode}
                                />
                                <BasicInput
                                    placeholder="Ville *"
                                    name="address.city"
                                    type="text"
                                    value={values.address.city}
                                    onChange={handleChange}
                                    error={errors?.address?.city}
                                    touched={touched.address?.city}
                                />
                                <BasicInput
                                    placeholder="Région"
                                    name="address.state"
                                    type="text"
                                    value={values.address.state}
                                    onChange={handleChange}
                                    error={errors?.address?.state}
                                    touched={touched.address?.state}
                                />
                                <div className="w-full">
                                    <CountryInput
                                        id="country"
                                        name="address.isoCountry"
                                        onChange={(e) => {
                                            handleCountryChange(e, setFieldValue);
                                            setFieldTouched('address.isoCountry', true, true);
                                        }}
                                        value={values.address.isoCountry}
                                        options={countriesOptions}
                                        className="w-full"
                                        error={errors?.address?.isoCountry}
                                        touched={touched?.address?.isoCountry}
                                        placeholder="Pays *"
                                    />

                                </div>
                                <BasicInput
                                    placeholder="Prénom du Représentant Légal"
                                    name="address.legalFirstname"
                                    type="text"
                                    value={values.address.legalFirstname}
                                    onChange={handleChange}
                                    error={errors?.address?.legalFirstname}
                                    touched={touched.address?.legalFirstname}
                                />
                                <BasicInput
                                    placeholder="Nom du Représentant Légal"
                                    name="address.legalLastname"
                                    type="text"
                                    value={values.address.legalLastname}
                                    onChange={handleChange}
                                    error={errors?.address?.legalLastname}
                                    touched={touched.address?.legalLastname}
                                />
                            </div>
                        </ExpandableSection>

                        <button className='btn btn-primary w-full mt-4' type="submit">CRÉER</button>
                    </Form>
                )
            }}
        </Formik>
    );
};

export default CreateCustomerForm;
